/**
 * Eski dünyaya açılan TEK kapı.
 *
 * Göç sırasında yeni modüller hâlâ js/*.js içindeki global'lere ihtiyaç duyuyor.
 * Bu dosya o bağımlılığı ortama dağılmış olmaktan çıkarıp tek yerde, tipli ve
 * SAYILABİLİR hâle getirir: köprüde ne varsa, eski koda kalan bağ odur.
 *
 * Göç ilerledikçe buradaki alanlar teker teker silinir. Dosya boşaldığında
 * eski çekirdeğe olan bağ da bitmiş demektir — ilerlemenin ölçüsü budur.
 *
 * Modüller global'lere ASLA doğrudan erişmez; hepsi buradan geçer.
 */

/** js/core.js — oturum açmış kullanıcı ve verisi. */
export interface CurrentUser {
  setup?: unknown;
  /** Supabase'deki profil kimliği. Profile bağlı her yazma bunu kullanır. */
  sbProfileId?: string | null;
  /** Bu profil AI'a açık mı (profiles.ai_enabled). */
  aiEnabled?: boolean;
  isManager?: boolean;
  name?: string;
  exp?: Array<{
    date: string;
    type: 'income' | 'expense';
    amount: number;
    cat: string;
    cur: string;
    note?: string;
    per?: string;
  }>;
}

/** Supabase istemcisinin modüllerin kullandığı kadarı. */
export interface SupabaseLike {
  from: (table: string) => any;
  rpc: (fn: string, args?: Record<string, unknown>) => any;
}

/** js/core.js sözlüğü. Arayüz yalnızca İngilizce (2026-09-16). */
export type TranslationBundle = { en: Record<string, string> };

interface LegacyGlobals {
  CU: CurrentUser | null;
  LNG: string;
  TDATA: TranslationBundle;
  gSF: () => CurrentUser['exp'];
  gbC: (rows: unknown[]) => Record<string, number>;
  T: (key: string) => string;
  esc: (s: string) => string;
  fm: (n: number) => string;
  toast: (msg: string) => void;
  go: (vid: string, nid: string) => void;
  authTab: (tab: string) => void;
  _sb: SupabaseLike | null;
  _apiBase: string;
  apiAuthFetch: (url: string, init?: RequestInit) => Promise<Response>;
}

const g = () => globalThis as unknown as Partial<LegacyGlobals>;

/** Eksik global'de sessizce yanlış davranmak yerine yüksek sesle patla. */
function need<K extends keyof LegacyGlobals>(key: K): LegacyGlobals[K] {
  const value = g()[key];
  if (value === undefined) {
    throw new Error(`köprü: global '${String(key)}' yok — js/*.js yüklendi mi?`);
  }
  return value as LegacyGlobals[K];
}

export const legacy = {
  /* durum */
  get user(): CurrentUser | null { return g().CU ?? null; },
  get lang(): string { return 'en'; },
  get isReady(): boolean { const u = g().CU; return !!(u && u.setup); },

  /* veri */
  scopedFinance: () => need('gSF')(),
  groupByCurrency: (rows: unknown[]) => need('gbC')(rows),

  /* biçimlendirme */
  t: (key: string) => need('T')(key),
  escapeHtml: (s: string) => need('esc')(s),
  money: (n: number) => need('fm')(n),

  /* eylemler */
  toast: (msg: string) => need('toast')(msg),
  navigate: (vid: string, nid: string) => need('go')(vid, nid),
  /** Worker ucuna oturum anahtarıyla POST. js/auth.js apiAuthFetch üstüne biner. */
  postApi: (path: string, body: unknown) => need('apiAuthFetch')(need('_apiBase') + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }),

  /** Giriş ekranındaki sekme (login / signup / forgot / reset). */
  authTab: (tab: string) => need('authTab')(tab),

  /**
   * Supabase istemcisi. Yoksa `null` — çağıran taraf kontrol etmeli.
   * Bilinçli olarak `need()` kullanılmıyor: bağlantı henüz kurulmamışken
   * modülün patlaması değil, sessizce beklemesi doğru davranış.
   */
  get sb(): SupabaseLike | null { return g()._sb ?? null; },

  /**
   * Modülün kendi metinlerini sözlüğe ekler.
   *
   * Mevcut anahtarların ÜZERİNE YAZMAZ: bir modülün kaldırılması ya da iki
   * modülün aynı anahtarı kullanması, çalışan bir metni bozmamalı.
   */
  addTranslations(bundle: TranslationBundle): void {
    const target = g().TDATA?.en;
    if (!target) return;                      // core.js henüz yüklenmedi
    for (const key of Object.keys(bundle.en)) {
      if (!(key in target)) target[key] = bundle.en[key];
    }
  },
};

/** Metin seçici. Arayüz tek dilli; imza, çağıranlar değişmesin diye duruyor. */
export function pick(texts: { en: string }): string {
  return texts.en;
}
